<template>
	<main-page :user="user" color="blue">
		<template v-slot:title> </template>

		<div class="container-fluid" style="text-align: left;">
			<div class="row mt-3">
				<div class="col-sm-3">
					<admin-nav></admin-nav>
				</div>

				<div class="col-sm-9">
					<div class="d-flex flex-row align-items-start">
						<div class="row flex mr-3">
							<div class="col-12 col-lg-4 mb-2 d-flex flex-row align-items-center">
								<i v-if="!filter" style="min-width: 15px;" class="fas fa-search mx-2" />
								<i
									v-if="filter"
									style="min-width: 15px; padding-left: 2px; padding-top: 2px;"
									class="fas fa-times mx-2 clickable"
									@click="filter = ''"
								/>
								<input
									class="form-control listing-search"
									placeholder="Search districts..."
									v-model="filter"
								/>
							</div>
						</div>
						<button @click="exportItems" class="btn btn-primary mr-3" v-tippy title="Export">
							<i class="fas fa-file-export" />
						</button>
						<a href="/#/districts/new" class="btn btn-primary mr-3">New</a>
						<b-pagination
							v-model="currentPage"
							:total-rows="totalRows"
							:per-page="perPage"
							style="float: right;"
						></b-pagination>
					</div>
					<b-table
						ref="table"
						:show-empty="true"
						:items="items"
						:fields="fields"
						:current-page="currentPage"
						:per-page="perPage"
						:filter="filter"
						:sort-by.sync="sortBy"
						:sort-desc.sync="sortDesc"
					>
						<template v-slot:cell(enabled)="data">
							<div v-if="data.item.enabled" class="text-success">Yes</div>
							<div v-if="!data.item.enabled" class="text-danger">No</div>
						</template>
						<template v-slot:cell(programs)="data">
							{{ (data.item.program_ids && data.item.program_ids.length) || 0 }}
						</template>
						<template v-slot:cell(schools)="data">
							{{ count("schools", data.item.id) }}
						</template>
						<template v-slot:cell(classes)="data">
							{{ count("sections", data.item.id) }}
						</template>
						<template v-slot:cell(teachers)="data">
							{{ count("teachers", data.item.id) }}
						</template>
						<template v-slot:cell(students)="data">
							{{ count("students", data.item.id) }}
						</template>
						<template v-slot:cell(partners)="data">
							{{ count("partners", data.item.id) }}
						</template>
						<template v-slot:cell(actions)="data">
							<div class="d-flex flex-row align-items-center">
								<a :href="`/#/districts/${data.item.id}`" class="mr-1 btn btn-inline btn-success"
									>Edit</a
								>
								<a @click="viewCodes(data.item.id)" class="mr-1 btn btn-inline btn-primary text-white">
									Code
								</a>
								<a
									@click="openInviteModal(data.item)"
									class="mr-1 btn btn-inline btn-warning text-white"
								>
									Invite
								</a>
								<confirm-delete
									typeName="District"
									:objectName="data.item.name"
									:objectId="data.item.id"
									:canDelete="service.canDeleteDistrict"
									:delete="service.deleteDistrict"
									:cleanup="$refs.table.refresh"
								>
									<button class="btn btn-inline btn-danger">Delete</button>
									<template v-slot:cantDeleteMessage>
										<h3>
											The District
											<span class="text-danger _600">"{{ data.item.name }}"</span>
											cannot be deleted
										</h3>
										<div class="mt-3 text-md text-muted">
											To preserve data integrity, districts that have been used to add users,
											classes, or schools are no longer deleteable
										</div>
									</template>
								</confirm-delete>
							</div>
						</template>
					</b-table>
				</div>
			</div>
		</div>

		<b-modal id="viewCodesModal" headerClass="indigo">
			<template v-slot:modal-title>
				<h3 class="modal-title full-bar-title">Join Codes</h3>
			</template>
			<div v-if="viewingDistrict" class="p-2">
				<div class="form-group pl-2 pr-3">
					<label>Teacher Join Code</label>
					<input
						type="text"
						v-model="viewingDistrict.join_code_teacher"
						readonly
						class="form-control join-code"
					/>
				</div>
			</div>
			<template v-slot:modal-footer="{ _, cancel }">
				<button type="button" class="btn btn-primary" @click="cancel">Done</button>
			</template>
		</b-modal>

		<b-modal id="inviteModal" headerClass="indigo">
			<template v-slot:modal-title>
				<h3 class="modal-title full-bar-title">Invite New Teacher</h3>
			</template>
			<div class="demargin">
				<div class="row mx-0" v-if="viewingDistrict">
					<div class="col-12 pt-4 px-4">
						<label class="text-md">Teacher Email</label>
						<input type="text" v-model="inviteEmail" class="form-control" />
					</div>
					<hr v-if="searchedEmail" class="w-100" />
					<div class="px-4 w-100">
						<div v-if="searchedEmail && validEmail" class="d-flex flex-column align-items-center">
							<div class="text-md mt-2">There is no user with the email</div>
							<div class="text-lg si-text-blue">{{ searchedEmail }}</div>
							<div class="text-md">currently in STEM Innovator®</div>
							<button class="btn btn-no-border btn-si-green mt-3 mb-4" @click="sendInvite">
								Send Invite Email
							</button>
						</div>
						<div v-if="searchedEmail && !validEmail" class="d-flex flex-column align-items-center">
							<div class="text-md mt-2">A user with the email</div>
							<div class="text-lg si-text-blue">{{ searchedEmail }}</div>
							<div class="text-md">already exists in STEM Innovator®</div>
							<button class="btn btn-no-border btn-si-gray mt-3 mb-4" disabled>
								Already Exists
							</button>
						</div>
					</div>
				</div>
			</div>
			<template v-slot:modal-footer="{ _, cancel }">
				<button type="button" class="btn btn-primary" @click="cancel">Close</button>
			</template>
		</b-modal>
	</main-page>
</template>

<script>
import MainPage from "@/components/MainPage";
import AdminNav from "@/vues/admin/AdminNav";
import ConfirmDelete from "@/components/ConfirmDelete";
import AdminService from "@/services/AdminService";
import FileService from "@/services/FileService";
import Notie from "@/services/NotieService";
import Store from "@/services/Store";
import _ from "lodash";

export default {
	name: "AdminLanding",

	props: ["user"],

	components: { MainPage, AdminNav, ConfirmDelete },

	data() {
		return {
			service: AdminService,

			filter: Store.get(this, "DistrictListing.filter"),
			currentPage: 1,
			perPage: 25,
			sortBy: "",
			sortDesc: false,
			totalRows: 1,

			viewingDistrict: null,

			inviteEmail: null,
			validEmail: false,
			searchedEmail: null,

			count_data: null,

			fields: [
				{ key: "name", label: "Name", sortable: true },
				{ key: "enabled", label: "Enabled" },
				{ key: "programs", label: "Programs", sortable: true },
				// { key: "created", label: "Created" },
				{ key: "schools", label: "Schools", sortable: true },
				{ key: "classes", label: "Classes", sortable: true },
				{ key: "teachers", label: "Teachers", sortable: true },
				{ key: "students", label: "Students", sortable: true },
				{ key: "partners", label: "Community Partners", sortable: true },
				{ key: "actions", label: "Actions" },
			],
		};
	},

	created() {
		this.search = _.debounce(this.doSearch, 250);
	},

	watch: {
		inviteEmail() {
			if (this.isPossibleEmail(this.inviteEmail)) {
				this.search();
			}
		},

		filter() {
			Store.set(this, "DistrictListing.filter", this.filter);
		},
	},

	methods: {

		count(key, id) {
			var item = _.find(this.count_data[key], (item) => {
				return item._id == id;
			});
			if (item && item.count) {
				return item.count;
			} else {
				return 0;
			}
		},

		items() {
			var pageOpts = {
				filter: this.filter,
				// query: query,
				page_size: this.perPage,
				current_page: this.currentPage,
				sort_by: this.sortBy,
				sort_desc: this.sortDesc,
				skip: (this.currentPage - 1) * this.perPage,
			};

			let timestamp = new Date().getTime();
			this.latest = timestamp;
			return new Promise((resolve, reject) => {
				this.service
					.listDistricts(pageOpts)
					.then((resp) => {
						if (timestamp != this.latest) {
							return;
						}

						console.log(resp);
						resolve(resp.data.result);
						this.totalRows = resp.data.count;

						this.service
							.getDistrictCounts()
							.then((resp) => {
								console.log(resp.data);
								this.count_data = resp.data;
							})
							.catch((e) => {
								console.log(e);
							});
					})
					.catch((e) => {
						reject(e);
					});
			});
		},

		exportItems() {
			var pageOpts = {
				filter: this.filter,
				// query: query,
				page_size: 0,
				current_page: 0,
				sort_by: this.sortBy,
				sort_desc: this.sortDesc,
				skip: 0,
			};

			this.service
				.listDistricts(pageOpts, this.role)
				.then((r) => {
					this.service.getDistrictCounts()
					.then((resp) => {
						console.log("ALL ITEMS", r.data.result);
						console.log("DATA", resp.data)
						FileService.exportDistricts(r.data.result, resp.data);
					})
				})
				.catch((e) => {
					console.error(e);
					Notie.error("Failed to export districts", e);
				});
		},

		viewCodes(id) {
			this.service
				.getDistrict(id)
				.then((r) => {
					this.viewingDistrict = r.data;
					this.$bvModal.show("viewCodesModal");
				})
				.catch((e) => {
					console.log(e);
					Notie.error("Failed to get district", e);
				});
		},

		doSearch() {
			let searchedEmail = this.inviteEmail;
			let opts = {
				query: { email: searchedEmail },
			};
			this.service
				.listUsers(opts)
				.then((r) => {
					this.searchedEmail = searchedEmail;
					console.log("EMAIL SEARCH", r.data.result);

					if (r.data.result.length > 0) {
						this.validEmail = false;
					} else {
						this.validEmail = true;
					}
				})
				.catch((e) => {
					console.log(e);
					Notie.error("Failed to search email", e);
				});
		},

		openInviteModal(district) {
			this.inviteEmail = null;
			this.searchedEmail = null;
			this.viewingDistrict = district;

			this.$bvModal.show("inviteModal");
		},

		isPossibleEmail(email) {
			if (!email) return false;
			if (email.includes(" ")) return false;
			if (!email.includes("@")) return false;
			let parts = email.split("@");
			if (parts.length != 2) return false;
			if (parts[0] == "") return false;
			if (parts[1] == "") return false;

			return true;
		},

		sendInvite() {
			if (!(this.viewingDistrict && this.searchedEmail)) {
				return;
			}

			AdminService.inviteTeacher(this.viewingDistrict.id, this.searchedEmail)
				.then(() => {
					Notie.info("Invite sent");
					this.$bvModal.hide("inviteModal");
				})
				.catch((e) => {
					console.log(e);
					Notie.error("Failed to send invite", e);
				});
		},
	},
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.join-code {
	font-size: 32px;
	text-align: center;
}
</style>
